import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  BellRing,
  BotMessageSquare,
  CalendarSync,
  CreditCard,
  Globe,
  MessageSquare,
  PlaneTakeoff,
  Repeat2,
  SquareCode,
} from "lucide-react";
import { cn } from "@ridereserv/shared/utils";

/**
 * One capability tile in the bento grid.
 *
 * `span` controls the desktop grid footprint (the grid is a 4-column,
 * 12-row-unit lattice on `lg`); below `lg` every tile collapses to a single
 * column. `feature` marks the anchor tile, which gets a denser, cobalt-tinted
 * "control surface" treatment. `label` is the small mono accent (e.g. "AI").
 */
interface BentoTile {
  icon: LucideIcon;
  label: string;
  title: string;
  description: string;
  /** Tailwind col/row-span classes for the `lg` lattice. */
  span: string;
  /** Anchor tile — denser, cobalt-tinted treatment. */
  feature?: boolean;
}

/**
 * Real, documented platform capabilities (no fabricated metrics). Sourced from
 * docs/data-model.md (tier feature flags), docs/features.md, docs/overview.md,
 * and docs/notifications.md. Ordering + spans build the asymmetric rhythm:
 * a tall AI anchor, a wide flight-tracking tile, and a run of unit tiles.
 */
const TILES: readonly BentoTile[] = [
  {
    icon: BotMessageSquare,
    label: "AI",
    title: "AI auto-dispatch",
    description:
      "An AI dispatcher reads each new reservation and proposes the right driver — so trips get assigned the moment they land, not when someone gets to the queue.",
    span: "lg:col-span-2 lg:row-span-2",
    feature: true,
  },
  {
    icon: PlaneTakeoff,
    label: "Realtime",
    title: "Flight tracking",
    description:
      "Airport pickups follow the flight. Live arrival status keeps drivers and customers aligned through delays and early landings — no manual refreshing.",
    span: "lg:col-span-2 lg:row-span-1",
  },
  {
    icon: BellRing,
    label: "Realtime",
    title: "Live status & notifications",
    description:
      "Every state change — confirmed, en route, completed — pushes instantly to the right people.",
    span: "lg:col-span-1 lg:row-span-1",
  },
  {
    icon: MessageSquare,
    label: "SMS",
    title: "SMS notifications",
    description:
      "Reach customers and drivers where they actually look. Text confirmations and reminders, governed by per-tier quotas.",
    span: "lg:col-span-1 lg:row-span-1",
  },
  {
    icon: SquareCode,
    label: "Embed",
    title: "Embeddable booking widget",
    description:
      "Drop a single snippet onto your site and take reservations in your own brand — no redirect, no app download.",
    span: "lg:col-span-2 lg:row-span-1",
  },
  {
    icon: Globe,
    label: "Brand",
    title: "Branded booking pages",
    description:
      "A hosted booking page with your logo and colors. Look like the operation you are from the first tap.",
    span: "lg:col-span-1 lg:row-span-1",
  },
  {
    icon: CreditCard,
    label: "Payments",
    title: "Online payments",
    description:
      "Collect card payments through Stripe Connect, with payouts routed straight to your account.",
    span: "lg:col-span-1 lg:row-span-1",
  },
  {
    icon: BarChart3,
    label: "Insights",
    title: "Analytics dashboard",
    description:
      "Revenue, ride volume, cancellations, and driver utilization — the operating picture in one view.",
    span: "lg:col-span-1 lg:row-span-1",
  },
  {
    icon: CalendarSync,
    label: "Sync",
    title: "Calendar sync",
    description:
      "Push reservations to Google or iCal so the schedule lives where your team already works.",
    span: "lg:col-span-1 lg:row-span-1",
  },
  {
    icon: Repeat2,
    label: "Repeat",
    title: "Recurring reservations",
    description:
      "Standing rides — the weekday commute, the recurring corporate run — booked once and scheduled on repeat.",
    span: "lg:col-span-2 lg:row-span-1",
  },
];

/**
 * The homepage capability "bento" — the 21st-century B2B SaaS centerpiece that
 * replaces the old thin three-card feature grid.
 *
 * An asymmetric CSS-grid lattice of real, documented platform capabilities
 * rendered as a dense "control surface": a cobalt-tinted AI anchor tile, a wide
 * flight-tracking tile, and a run of unit tiles. Flat hairline chrome,
 * token-only colors, and fully flip-safe for dark mode. On mobile the lattice
 * collapses to a single column.
 *
 * Standalone and prop-free; the homepage integration unit wires it in.
 * @returns The feature bento section.
 */
export function FeatureBentoSection() {
  return (
    <section
      aria-labelledby="feature-bento-heading"
      className="border-b border-neutral-200 bg-background"
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24 lg:px-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-surface px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-neutral-600">
            <span aria-hidden className="size-1.5 rounded-full bg-primary-500" />
            The platform
          </span>
          <h2
            id="feature-bento-heading"
            className="mt-5 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl"
          >
            One console to run your whole operation
          </h2>
          <p className="mt-3 text-lg text-neutral-600">
            From the first booking to the final payout — dispatch, tracking,
            payments, and customer comms in a single, AI-native workspace built
            for independent drivers and growing fleets alike.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:auto-rows-[minmax(0,1fr)] lg:grid-cols-4">
          {TILES.map((tile) => (
            <BentoCard key={tile.title} tile={tile} />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * A single bento tile. The anchor (`feature`) tile uses a cobalt-tinted ground
 * with a faint hairline "console grid" texture and an active cobalt icon; unit
 * tiles use a flat surface with a monochrome hairline icon circle that warms to
 * cobalt on group hover.
 * @param root0 - Component props.
 * @param root0.tile - The tile content + layout descriptor.
 * @returns The bento tile element.
 */
function BentoCard({ tile }: { tile: BentoTile }) {
  const { icon: Icon, label, title, description, span, feature } = tile;

  return (
    <article
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-md border p-5 transition-colors",
        span,
        feature
          ? "border-primary-200 bg-primary-50/60 hover:border-primary-300"
          : "border-neutral-200 bg-surface hover:border-neutral-300"
      )}
    >
      {/* Signature detail: faint cobalt console-grid texture on the anchor tile
          only. The grid lines are token-colored (primary-200); a radial mask
          fades them out toward the bottom-right so the copy stays legible. The
          mask color is irrelevant (masks key off alpha), so it is flip-safe. */}
      {feature ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60 [background-image:linear-gradient(to_right,var(--color-primary-200)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-primary-200)_1px,transparent_1px)] [background-size:28px_28px] [mask-image:radial-gradient(120%_100%_at_0%_0%,black,transparent_72%)]"
        />
      ) : null}

      <div className="relative flex items-center justify-between gap-3">
        <span
          aria-hidden
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full border transition-colors",
            feature
              ? "h-11 w-11 border-primary-200 bg-surface text-primary-600 dark:text-primary-400"
              : "h-10 w-10 border-neutral-200 text-neutral-400 group-hover:border-primary-200 group-hover:text-primary-600"
          )}
        >
          <Icon
            className={feature ? "h-5 w-5" : "h-4 w-4"}
            strokeWidth={1.75}
          />
        </span>
        <span
          className={cn(
            "rounded-sm border px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em]",
            feature
              ? "border-primary-200 bg-surface/70 text-primary-700"
              : "border-neutral-200 bg-background text-neutral-500"
          )}
        >
          {label}
        </span>
      </div>

      <h3
        className={cn(
          "relative mt-4 font-semibold tracking-tight text-neutral-900",
          feature ? "text-xl" : "text-base"
        )}
      >
        {title}
      </h3>
      <p
        className={cn(
          "relative mt-1.5 text-neutral-600",
          feature ? "text-base" : "text-sm"
        )}
      >
        {description}
      </p>
    </article>
  );
}
